import { maybe } from '@passionware/monads';
import { ApiClient } from 'lib/api/ApiClient.types';
import { convertCoreFieldFiltersToHttp } from 'v5/core/core-fields/core-fields-filters.http-converters';
import { convertCustomFieldFiltersToHttp } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import { MergeServices, WithServices } from 'v5/platform/ts/services';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { z } from 'zod';
import { WithWorkspace } from '../_common/query/workspace';
import { mergeWorkspaceScopingIntoHttpFilters } from 'v5/core/app-scope/query/mergeWorkspaceScopingIntoHttpFilters';
import { mergeTeamScopingIntoHttpFilters } from 'v5/core/app-scope/query/mergeTeamScopingIntoHttpFilters';
import {
  asGlobalProductionSearchQuery,
  ProductionApi,
  ProductionAssociationDocument,
  ProductionAssociationNote,
  ProductionAssociationsBundle,
  ProductionBatchIdsQuery,
  ProductionListResponse,
  ProductionQuery,
  ProductionScopedSearchQuery,
  ProductionSearchQuery,
  TransferProductionsToScopeRequest
} from './production.api';
import {
  production$,
  productionBatchSearchListResponse$,
  productionFromHttp,
  productionSearchListResponse$
} from './production.http.schema';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';

const API_URL_V1 = import.meta.env.VITE_API_URL;

function unwrapTopLevelData(body: unknown): unknown {
  if (
    body &&
    typeof body === 'object' &&
    'data' in body &&
    (body as { data: unknown }).data != null
  ) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapAssociationsPayload(body: unknown): unknown {
  return unwrapTopLevelData(body);
}

const productionAssociationNoteFromCreate$ = z
  .object({
    id: z.coerce.number(),
    note: z.string(),
    created_at: z.string(),
    user_id: z.union([z.string(), z.number()]).optional(),
    user: z.record(z.string(), z.unknown()).optional(),
    archived: z.boolean().optional()
  })
  .passthrough();

const createProductionNoteResponse$ = z.preprocess(
  body => unwrapTopLevelData(body),
  productionAssociationNoteFromCreate$
);

const productionAssociationDocumentFileFromCreate$ = z.object({
  // The server sends a number.
  id: z.coerce.string().optional(),
  name: z.string(),
  mime_type: z.string().optional()
});

const productionAssociationDocumentFromCreate$ = z
  .object({
    id: z.coerce.number(),
    url: z.string().optional(),
    archived: z.boolean().optional(),
    file: productionAssociationDocumentFileFromCreate$.optional()
  })
  .passthrough();

const createProductionDocumentResponse$ = z.preprocess(
  body => unwrapTopLevelData(body),
  productionAssociationDocumentFromCreate$
);

function parseCreateProductionNoteResponse(
  body: unknown
): ProductionAssociationNote {
  return parseWithDecoratedError(createProductionNoteResponse$, body);
}

function parseCreateProductionDocumentResponse(
  body: unknown
): ProductionAssociationDocument {
  return parseWithDecoratedError(createProductionDocumentResponse$, body);
}

function isAssociationsRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

function normalizeProductionAssociations(
  body: unknown
): ProductionAssociationsBundle {
  const payload = unwrapAssociationsPayload(body);
  if (!isAssociationsRecord(payload)) {
    return { notes: [], documents: [], raw: {} };
  }
  const notesRaw = payload.notes;
  const docsRaw = payload.documents;
  const notes = Array.isArray(notesRaw)
    ? notesRaw
        .filter(isAssociationsRecord)
        .map(n => {
          const id = Number(n.id);
          const note =
            typeof n.note === 'string' ? n.note : String(n.note ?? '');
          const created_at =
            typeof n.created_at === 'string' ? n.created_at : '';
          if (!Number.isFinite(id) || !created_at) {
            return null;
          }
          return {
            ...n,
            id,
            note,
            created_at
          } as ProductionAssociationsBundle['notes'][number];
        })
        .filter(
          (n): n is ProductionAssociationsBundle['notes'][number] => n !== null
        )
    : [];
  const documents = Array.isArray(docsRaw)
    ? docsRaw
        .filter(isAssociationsRecord)
        .map(d => {
          const id = Number(d.id);
          if (!Number.isFinite(id)) {
            return null;
          }
          return {
            ...d,
            id
          } as ProductionAssociationsBundle['documents'][number];
        })
        .filter(
          (d): d is ProductionAssociationsBundle['documents'][number] =>
            d !== null
        )
    : [];
  return { notes, documents, raw: payload };
}

async function getBatchProductions(
  client: ApiClient,
  ids: number[]
): Promise<ProductionListResponse> {
  if (ids.length === 0) {
    return {
      items: [],
      count: 0
    };
  }

  const response = await client.post(`/batch/productions/search`, {
    data: { ids },
    token: true
  });

  const parsed = parseWithDecoratedError(
    productionBatchSearchListResponse$,
    response
  );
  return {
    items: rowLevelValidation.unsafeMapAll(parsed.data, productionFromHttp),
    count: parsed.data.length
  };
}

async function searchProductions(
  client: ApiClient,
  services: MergeServices<[WithMetastructureMappingService]>,
  query: WithWorkspace<ProductionSearchQuery | ProductionScopedSearchQuery>
): Promise<ProductionListResponse> {
  // Get field type mappings for custom fields
  const fieldMappings =
    await services.metastructureMappingService.getAllFieldNameTypeMappings();

  const scopedQuery: ProductionScopedSearchQuery =
    'workspaceId' in query
      ? (query as ProductionScopedSearchQuery)
      : asGlobalProductionSearchQuery(query);

  const filters = {
    ...convertCoreFieldFiltersToHttp(scopedQuery.filters),
    // Convert custom field filters using the field type mappings
    ...convertCustomFieldFiltersToHttp(
      fieldMappings,
      scopedQuery.filters,
      scopedQuery.workspaceId
    ),
    ...mergeWorkspaceScopingIntoHttpFilters(scopedQuery),
    ...mergeTeamScopingIntoHttpFilters(scopedQuery)
  };

  // Map frontend sort fields to backend field names
  // Note: Most production fields are stored in the JSONB 'attributes' column
  // and require the full JSONB path syntax for sorting
  const mapSortFieldToApi = (field: string): string => {
    const fieldMap: Record<string, string> = {
      createdAt: 'created_at',
      updatedAt: 'updated_at',
      // JSONB attribute fields require the full path
      title: "attributes->>'title'",
      code: "attributes->>'code'",
      productionDate: "attributes->>'production_start_date'",
      status: "attributes->>'status_id'" // Note: sorts by ID, not status name
    };
    return fieldMap[field] ?? field;
  };

  const requestBody: Record<string, unknown> = {
    query: scopedQuery.search,
    p: scopedQuery.page.page + 1,
    count: scopedQuery.page.pageSize,
    // Note: 'archived' is at root level, not in filters, per backend API requirements
    archived: scopedQuery.filters?.archived?.value ? true : false,
    order_by: scopedQuery.sort
      ? {
          field: mapSortFieldToApi(scopedQuery.sort.field),
          direction: scopedQuery.sort.order.toUpperCase(),
          enterprise_workspace_id: scopedQuery.workspaceId
        }
      : {
          field: 'created_at',
          direction: 'DESC',
          enterprise_workspace_id: scopedQuery.workspaceId
        },
    includeResource: true,
    filters
  };

  const response = await client.post(
    `${API_URL_V1}/workspaces/${query.workspace}/search/productions`,
    {
      data: requestBody,
      token: true
    }
  );

  const parsed = parseWithDecoratedError(
    productionSearchListResponse$,
    response
  );
  return {
    items: rowLevelValidation.unsafeMapAll(parsed.results, productionFromHttp),
    count: parsed.paging.total
  };
}

function isBatchIdsQuery(
  query: WithWorkspace<ProductionQuery>
): query is WithWorkspace<ProductionBatchIdsQuery> {
  return (
    query.filters !== null &&
    query.filters !== undefined &&
    'id' in query.filters
  );
}

export function createProductionApi(
  config: { client: ApiClient } & WithServices<
    [WithMetastructureMappingService]
  >
): ProductionApi {
  const { client, services } = config;

  return {
    getProductions: async query => {
      // Check if this is an id-based query (batch search)
      if (isBatchIdsQuery(query)) {
        if (maybe.isAbsent(query.filters.id)) {
          throw new Error('id filter is required');
        }
        if (query.filters.id.operator === 'matchNone') {
          throw new Error('matchNone is not supported for id filter');
        }
        return getBatchProductions(client, query.filters.id.value);
      }

      // Otherwise, use the regular search endpoint
      return searchProductions(client, services, query);
    },

    getProduction: async id => {
      const response = await client.get(
        `${import.meta.env.VITE_API_URL_V3}/:workspace_id/productions/${id}`,
        {
          token: true
        }
      );

      const parsed = z.object({ data: production$ }).parse(response);
      return productionFromHttp(parsed.data);
    },

    getProductionAssociations: async productionId => {
      const body = await client.get<unknown>(
        `/productions/${productionId}/associations`,
        { token: true }
      );
      return normalizeProductionAssociations(body);
    },

    createProductionNote: async (productionId, payload) => {
      const response = await client.post<unknown>(
        `/productions/${productionId}/associations/notes`,
        { data: payload, token: true }
      );
      return parseCreateProductionNoteResponse(response);
    },

    deleteProductionNote: async (productionId, noteId) => {
      await client.del<unknown>(
        `/productions/${productionId}/associations/notes/${noteId}`,
        { token: true }
      );
    },

    createProductionDocument: async (productionId, payload) => {
      const response = await client.post<unknown>(
        `/productions/${productionId}/associations/files`,
        { data: payload, token: true }
      );
      return parseCreateProductionDocumentResponse(response);
    },

    deleteProductionDocument: async (productionId, documentId) => {
      await client.del<unknown>(
        `/productions/${productionId}/associations/files/${documentId}`,
        { token: true }
      );
    },

    reorderDocuments: async orderedIds => {
      await client.put<unknown>('/reorder/documents', {
        data: { reorder: orderedIds.map((id, order) => ({ id, order })) },
        token: true
      });
    },

    transferProductionsToScope: async (
      request: TransferProductionsToScopeRequest,
      signal?: AbortSignal
    ) => {
      await client.post('/batch/productions/transfer', {
        data: request,
        token: true,
        signal
      });
    }
  };
}
